import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import type { TemplateDef, FormValues, FieldDef, SectionDef } from '../types';
import FormSection from './FormSection';
import { validateDates } from '../../shared/utils/dateUtils';
import AgentChatWidget from './AgentChatWidget';
import { createBriefingPromocao } from '../services/briefingPromocoesApi';
import {
  exportRebaixaSimples,
  exportRebaixaProgressiva,
  exportCompreEGanhe,
  exportListaSugeridaOriginal,
  exportListaSugeridaRecompra,
  exportCupomSimples,
  exportCupomProgressivo,
  exportCashback,
  exportRebaixaCupomGeral,
} from '../../shared/utils/xlsxExport';

interface PromotionFormProps {
  template: TemplateDef;
  onReset: () => void;
  initialValues?: FormValues;
  reuseSourceId?: string | number;
}

const HISTORY_ROUTE = '/briefing-promocoes/historico';
const DEFAULT_FORM_VALUES: FormValues = {
  tipoSolicitacao: '',
  idIFC: 'n/a',
};

const XLSX_EXPORTERS: Record<string, (values: FormValues) => void> = {
  t1: exportRebaixaSimples,
  t2: exportRebaixaProgressiva,
  t3: exportCompreEGanhe,
  t4a: exportListaSugeridaOriginal,
  t4b: exportListaSugeridaRecompra,
  t5: exportCupomSimples,
  t6: exportCupomProgressivo,
  t7: exportCashback,
  t8: exportRebaixaCupomGeral,
};

const CHILD_COUNT_KEY = 'qtdCardsFilhos';
const CHILD_MAX_COUNT = 4;
const CHILD_FIELD_SUFFIX_RE = /__child(\d+)$/;

const childCountField: FieldDef = {
  key: CHILD_COUNT_KEY,
  label: 'Quantidade de cards filhos',
  type: 'number',
  placeholder: '0 a 4',
  helperText: 'Ao informar filhos, serão exibidos blocos adicionais.',
  conditional: { field: 'numeroCard', values: ['1'] },
};

const CHILD_REPEAT_SECTIONS_BY_TEMPLATE: Record<string, string[]> = {
  t3: ['Cards e Desconto', 'Gatilho da Família', 'Configuração da Família'],
  t4a: ['Cards e Estrutura', 'Configuração da Família'],
  t4b: ['Cards e Estrutura', 'Configuração da Família'],
  t6: ['Cards e Desconto', 'Faixa de Desconto', 'Produtos'],
  t8: ['Cards e Desconto', 'Critério de Desconto', 'Inclusão e Exclusão de Produtos', 'Itens Obrigatórios'],
};
const CARD_CONTROLLER_SECTION_TITLES = ['Cards e Desconto', 'Cards e Estrutura'];

function normalizeChildCount(value: unknown): number {
  if (Array.isArray(value)) return 0;
  const parsed = parseInt(String(value ?? '').replace(/[^0-9]/g, ''), 10);
  if (Number.isNaN(parsed)) return 0;
  return Math.max(0, Math.min(CHILD_MAX_COUNT, parsed));
}

function childFieldKey(fieldKey: string, childNumber: number): string {
  return `${fieldKey}__child${childNumber}`;
}

function childNumberFromKey(fieldKey: string): number | null {
  const match = fieldKey.match(CHILD_FIELD_SUFFIX_RE);
  if (!match) return null;
  const parsed = parseInt(match[1], 10);
  return Number.isNaN(parsed) ? null : parsed;
}

function baseFieldKey(fieldKey: string): string {
  return fieldKey.replace(CHILD_FIELD_SUFFIX_RE, '');
}

function removeChildValues(values: FormValues) {
  Object.keys(values).forEach((key) => {
    if (key === CHILD_COUNT_KEY || childNumberFromKey(key) !== null) {
      delete values[key];
    }
  });
}

function syncChildCardValues(values: FormValues) {
  if (values.numeroCard !== '1') {
    removeChildValues(values);
    return;
  }

  const count = normalizeChildCount(values[CHILD_COUNT_KEY]);
  values[CHILD_COUNT_KEY] = count > 0 ? String(count) : '';

  Object.keys(values).forEach((key) => {
    const childNumber = childNumberFromKey(key);
    if (childNumber !== null && (childNumber < 2 || childNumber > count + 1)) {
      delete values[key];
    }
  });

  for (let childNumber = 2; childNumber <= count + 1; childNumber += 1) {
    values[childFieldKey('numeroCard', childNumber)] = String(childNumber);
  }
}

function normalizeChangedValue(key: string, value: string | string[]): string | string[] {
  if (key === CHILD_COUNT_KEY) {
    const count = normalizeChildCount(value);
    return count > 0 ? String(count) : '';
  }

  const childNumber = childNumberFromKey(key);
  if (baseFieldKey(key) === 'numeroCard' && childNumber !== null) {
    return String(childNumber);
  }

  return value;
}

function cloneFieldForChild(field: FieldDef, childNumber: number): FieldDef {
  const helperText = field.helperText?.includes('card pai')
    ? 'Preencher para este card filho.'
    : field.helperText;

  const cloned: FieldDef = {
    ...field,
    key: childFieldKey(field.key, childNumber),
    helperText,
    conditional: field.conditional
      ? {
          ...field.conditional,
          field: childFieldKey(field.conditional.field, childNumber),
        }
      : undefined,
    excludeValuesFrom: field.excludeValuesFrom
      ? childFieldKey(field.excludeValuesFrom, childNumber)
      : undefined,
  };

  if (field.key === 'numeroCard') {
    const fixedOption = field.options?.find((option) => option.value === String(childNumber));
    cloned.options = [
      fixedOption ?? {
        value: String(childNumber),
        label: `${childNumber} - Card filho ${childNumber}`,
      },
    ];
    cloned.helperText = 'Card filho definido automaticamente por este bloco.';
  }

  return cloned;
}

function findSectionByTitle(sections: SectionDef[], title: string): SectionDef | undefined {
  return sections.find((section) => section.title === title);
}

function getChildRepeatSectionTitles(templateId: string): string[] {
  return CHILD_REPEAT_SECTIONS_BY_TEMPLATE[templateId] ?? [];
}

function hasCardNumberField(section: SectionDef): boolean {
  return section.fields.some((field) => field.key === 'numeroCard');
}

function getCardsSection(templateId: string, sections: SectionDef[]): SectionDef | undefined {
  const cardSectionTitle = getChildRepeatSectionTitles(templateId)[0];
  if (!cardSectionTitle) return undefined;

  const configuredSection = findSectionByTitle(sections, cardSectionTitle);
  if (configuredSection && hasCardNumberField(configuredSection)) return configuredSection;

  return sections.find(
    (section) => CARD_CONTROLLER_SECTION_TITLES.includes(section.title) && hasCardNumberField(section),
  );
}

function getChildRepeatSections(templateId: string, sections: SectionDef[]): SectionDef[] {
  const repeatSectionTitles = new Set(getChildRepeatSectionTitles(templateId));
  const cardsSection = getCardsSection(templateId, sections);
  if (!cardsSection) return [];
  repeatSectionTitles.add(cardsSection.title);

  return sections.filter((section) => repeatSectionTitles.has(section.title));
}

function buildRenderedSections(templateId: string, sections: SectionDef[], values: FormValues): SectionDef[] {
  const repeatSections = getChildRepeatSections(templateId, sections);
  const cardsSection = getCardsSection(templateId, sections);
  if (repeatSections.length === 0) return sections;

  const baseSections = sections.map((section) => {
    if (section.title !== cardsSection?.title) return section;
    if (section.fields.some((field) => field.key === CHILD_COUNT_KEY)) return section;

    return {
      ...section,
      fields: [...section.fields, childCountField],
    };
  });

  const childCount = values.numeroCard === '1' ? normalizeChildCount(values[CHILD_COUNT_KEY]) : 0;
  if (childCount <= 0) return baseSections;

  const childSections: SectionDef[] = [];

  for (let childNumber = 2; childNumber <= childCount + 1; childNumber += 1) {
    repeatSections.forEach((section) => {
      childSections.push({
        ...section,
        title: `Card filho ${childNumber} - ${section.title}`,
        fields: section.fields.map((field) => cloneFieldForChild(field, childNumber)),
      });
    });
  }

  return [...baseSections, ...childSections];
}

function cloneFormValues(values: FormValues): FormValues {
  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => [key, Array.isArray(value) ? [...value] : value]),
  ) as FormValues;
}

function getInitialFormValues(initialValues?: FormValues): FormValues {
  const values = {
    ...DEFAULT_FORM_VALUES,
    ...(initialValues ? cloneFormValues(initialValues) : {}),
  };

  syncChildCardValues(values);

  return values;
}

export default function PromotionForm({ template, onReset, initialValues, reuseSourceId }: PromotionFormProps) {
  const navigate = useNavigate();
  const [values, setValues] = useState<FormValues>(() => getInitialFormValues(initialValues));
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | number | undefined>(undefined);
  const [expandedSections, setExpandedSections] = useState<boolean[]>(
    () => template.sections.map(() => true),
  );

  const renderedSections = useMemo(
    () => buildRenderedSections(template.id, template.sections, values),
    [template.id, template.sections, values]
  );
  const renderedSectionCount = renderedSections.length;

  const allExpanded = renderedSections.length > 0 && renderedSections.every((_, idx) => expandedSections[idx] ?? true);
  const allCollapsed = renderedSections.length > 0 && renderedSections.every((_, idx) => !(expandedSections[idx] ?? true));

  useEffect(() => {
    const nextValues = getInitialFormValues(initialValues);

    setValues(nextValues);
    setSubmitted(false);
    setSavedId(undefined);
    setSaveError(null);
    setExpandedSections(buildRenderedSections(template.id, template.sections, nextValues).map(() => true));
  }, [template.id, initialValues]);

  useEffect(() => {
    setExpandedSections((prev) => Array.from({ length: renderedSectionCount }, (_, idx) => prev[idx] ?? true));
  }, [renderedSectionCount]);

  const handleChange = (key: string, value: string | string[]) => {
    setValues((prev) => {
      const normalizedValue = normalizeChangedValue(key, value);
      const next = { ...prev, [key]: normalizedValue };

      if (key === 'nomeExibicao' && typeof value === 'string') {
        const previousDisplayName = typeof prev.nomeExibicao === 'string' ? prev.nomeExibicao : '';
        const currentDescription = typeof prev.descricao === 'string' ? prev.descricao : '';
        const currentInternalDescription = typeof prev.descricaoInterna === 'string' ? prev.descricaoInterna : '';

        if (!currentDescription.trim() || currentDescription === previousDisplayName) {
          next.descricao = value;
        }

        if (!currentInternalDescription.trim() || currentInternalDescription === previousDisplayName) {
          next.descricaoInterna = value;
        }
      }

      syncChildCardValues(next);

      return next;
    });
    if (submitted) setSubmitted(false);
    if (saveError) setSaveError(null);
  };

  const handleToggleSection = (idx: number) => {
    setExpandedSections((prev) => prev.map((v, i) => (i === idx ? !v : v)));
  };

  const handleExpandAll = () => setExpandedSections(renderedSections.map(() => true));
  const handleCollapseAll = () => setExpandedSections(renderedSections.map(() => false));

  const dateErrors = useMemo(() => {
    const start = (values['inicioVigencia'] as string) ?? '';
    const end = (values['fimVigencia'] as string) ?? '';
    const { startError, endError } = validateDates(start, end);
    const errors: Record<string, string> = {};
    if (startError) errors['inicioVigencia'] = startError;
    if (endError) errors['fimVigencia'] = endError;
    return errors;
  }, [values]);

  const handleSubmit = async () => {
    setSubmitted(false);
    setSaveError(null);

    if (Object.keys(dateErrors).length > 0) {
      setSaveError('Corrija os campos de vigência antes de salvar o briefing.');
      return;
    }

    try {
      setSaving(true);
      const id = await createBriefingPromocao(template.id, values);
      setSavedId(id);
      setSubmitted(true);

      if (id) {
        navigate(`${HISTORY_ROUTE}?briefingId=${id}`, {
          state: {
            selectedBriefingId: String(id),
            openBriefing: true,
            created: true,
          },
        });
      }
    } catch (error) {
      console.log(error);
      setSaveError(error instanceof Error ? error.message : 'Não foi possível salvar o briefing.');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyJSON = () => {
    navigator.clipboard.writeText(JSON.stringify(values, null, 2));
  };

  const handleClearForm = () => {
    setValues(getInitialFormValues());
    setSubmitted(false);
    setSavedId(undefined);
    setSaveError(null);
  };

  function normalizeText(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function findOptionValue(rawValue: unknown, options?: { value: string; label: string }[]): string {
  if (rawValue === undefined || rawValue === null) return '';

  const value = String(rawValue).trim();
  if (!value) return '';

  if (!options || options.length === 0) return value;

  const directMatch = options.find((opt) => String(opt.value) === value);
  if (directMatch) return directMatch.value;

  const normalizedValue = normalizeText(value);

  const labelMatch = options.find((opt) => {
    const normalizedLabel = normalizeText(opt.label);
    const normalizedOptionValue = normalizeText(opt.value);

    return (
      normalizedLabel === normalizedValue ||
      normalizedOptionValue === normalizedValue ||
      normalizedLabel.includes(normalizedValue) ||
      normalizedValue.includes(normalizedLabel)
    );
  });

  if (labelMatch) return labelMatch.value;

  const startsWithNumber = value.match(/^(\d+)/);
  if (startsWithNumber) {
    const numberMatch = options.find((opt) => opt.value === startsWithNumber[1]);
    if (numberMatch) return numberMatch.value;
  }

  return value;
}

function normalizeAgentValues(values: FormValues, template: TemplateDef): FormValues {
  const fieldMap = new Map(
    template.sections.flatMap((section) =>
      section.fields.map((field) => [field.key, field])
    )
  );

  const normalized: FormValues = {};

  Object.entries(values).forEach(([key, rawValue]) => {
    const field = fieldMap.get(key);

    if (!field) {
      normalized[key] = rawValue;
      return;
    }

    if (field.type === 'select') {
      normalized[key] = findOptionValue(rawValue, field.options);
      return;
    }

    if (field.type === 'multiselect') {
      const rawList = Array.isArray(rawValue)
        ? rawValue
        : String(rawValue ?? '')
            .split(/[,;\n]+/)
            .map((item) => item.trim())
            .filter(Boolean);

      normalized[key] = rawList
        .map((item) => findOptionValue(item, field.options))
        .filter(Boolean);

      return;
    }

    if (Array.isArray(rawValue)) {
      normalized[key] = rawValue.map((item) => String(item).trim()).filter(Boolean);
      return;
    }

    normalized[key] = String(rawValue ?? '').trim();
  });

  return normalized;
}

  const handleFillFromAgent = (newValues: FormValues) => {
    const normalizedValues = normalizeAgentValues(newValues, template);

    setValues((prev) => {
      const next = {
        ...prev,
        ...normalizedValues,
      };

      syncChildCardValues(next);

      return next;
    });

    setSubmitted(false);
    setSavedId(undefined);
    setSaveError(null);
  };

  const handleExportXLSX = () => {
    const exporter = XLSX_EXPORTERS[template.id];
    if (exporter) exporter(values);
  };

  const hasXlsxExport = template.id in XLSX_EXPORTERS;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
          pb: 2.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: template.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 800, fontSize: '0.9rem' }}>
              {template.number}
            </Typography>
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {template.name}
            </Typography>
            <Chip
              label={template.mechanicsBadge}
              size="small"
              sx={{
                mt: 0.5,
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 600,
                bgcolor: `${template.color}15`,
                color: template.color,
                letterSpacing: '0.3px',
              }}
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title={allExpanded ? 'Todos os campos já estão abertos' : 'Expandir todas as seções'}>
            <span>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<UnfoldMoreIcon fontSize="small" />}
                onClick={handleExpandAll}
                disabled={allExpanded}
                sx={{ color: 'text.secondary', borderColor: 'divider' }}
              >
                Expandir todos
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={allCollapsed ? 'Todos os campos já estão fechados' : 'Recolher todas as seções'}>
            <span>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<UnfoldLessIcon fontSize="small" />}
                onClick={handleCollapseAll}
                disabled={allCollapsed}
                sx={{ color: 'text.secondary', borderColor: 'divider' }}
              >
                Recolher todos
              </Button>
            </span>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<ContentCopyOutlinedIcon fontSize="small" />}
            onClick={handleCopyJSON}
            sx={{ color: 'text.secondary', borderColor: 'divider' }}
          >
            Copiar JSON
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<RestartAltIcon fontSize="small" />}
            onClick={handleClearForm}
            sx={{ color: 'text.secondary', borderColor: 'divider' }}
          >
            Limpar
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="warning"
            onClick={onReset}
          >
            Trocar template
          </Button>
        </Box>
      </Box>

      {reuseSourceId !== undefined && reuseSourceId !== null && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Reaproveitando dados do briefing {reuseSourceId}. Ao salvar, será criado um novo briefing.
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {renderedSections.map((section, idx) => (
          <FormSection
            key={`${section.title}-${idx}`}
            section={section}
            index={idx}
            values={values}
            onChange={handleChange}
            accentColor={template.color}
            fieldErrors={dateErrors}
            expanded={expandedSections[idx] ?? true}
            onToggle={() => handleToggleSection(idx)}
          />
        ))}
      </Box>

      <Divider sx={{ my: 3 }} />

      {submitted && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Briefing salvo com sucesso!{savedId ? ` ID: ${savedId}.` : ''} Campos preenchidos: {Object.keys(values).filter((k) => values[k] && values[k] !== '').length}
        </Alert>
      )}

      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleClearForm}
          sx={{ color: 'text.secondary', borderColor: 'divider' }}
        >
          Limpar formulário
        </Button>
        {/*hasXlsxExport && (
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={handleExportXLSX}
            sx={{
              borderColor: template.color,
              color: template.color,
              '&:hover': {
                borderColor: template.color,
                bgcolor: `${template.color}10`,
              },
            }}
          >
            Exportar XLSX
          </Button>
        )*/}
        <Button
          variant="contained"
          startIcon={<SaveOutlinedIcon />}
          onClick={handleSubmit}
          disabled={saving}
          sx={{ bgcolor: template.color, '&:hover': { bgcolor: template.color, filter: 'brightness(0.9)' } }}
        >
          {saving ? 'Salvando...' : 'Salvar briefing'}
        </Button>
      </Box>

      <AgentChatWidget
  templateId={template.id}
  templateName={template.name}
  currentValues={values}
  onFillForm={handleFillFromAgent}
/>
    </Box>
  );
}
